import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "@/i18n";

interface SEOProps {
  title?: string;
  description?: string;
  path?: string;
  image?: string;
  noindex?: boolean;
}

const BASE_URL = "https://www.inkandinsanity.com";
const DEFAULT_IMAGE = `${BASE_URL}/InkandInsanity-OG-Image.png`;

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  path = "",
  image = DEFAULT_IMAGE,
  noindex = false,
}) => {
  const { t } = useTranslation("common");
  const siteSuffix = t("seo.titleSuffix");
  const defaultTitle = t("seo.defaultTitle");
  const resolvedDescription = description ?? t("seo.defaultDescription");
  const fullTitle = title ? `${title} | ${siteSuffix}` : defaultTitle;
  const canonicalUrl = `${BASE_URL}${path}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={resolvedDescription} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={canonicalUrl} />

      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
      {SUPPORTED_LANGUAGES.map((lng) => (
        <link key={lng} rel="alternate" hrefLang={lng} href={canonicalUrl} />
      ))}

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:type" content="website" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
};

export default SEO;
